import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Clock,
  DollarSign,
  Truck,
  CheckCircle2,
  AlertCircle,
  Building2,
  Brain,
  Send,
  Loader2,
  FileText,
  Hash,
  Calendar,
  Check,
  X,
  AlertTriangle,
  HelpCircle,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupplierQuotePortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [supplierNotes, setSupplierNotes] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [ackStatus, setAckStatus] = useState("");
  const [ackNotes, setAckNotes] = useState("");

  useEffect(() => {
    const fetchRequestData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/suppliers/quote/${token}`);
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.detail || "Failed to load request");
        }
        const data = await res.json();
        setRequestData(data);
        
        // Initialize quotes state for each item with enhanced fields
        const initialQuotes = {};
        data.items.forEach((item) => {
          const existingQuote = data.existing_quotes?.find(q => q.item_id === item.id);
          initialQuotes[item.id] = {
            item_name: item.item_name || item.name || "",
            original_or_substitute: existingQuote?.original_or_substitute || "original",
            manufacturer_number: existingQuote?.manufacturer_number || item.mfr_number || "",
            description: existingQuote?.description || "",
            quantity_available: existingQuote?.quantity_available || item.quantity,
            cost: existingQuote?.supplier_cost || existingQuote?.unit_price || "",
            freight_included: existingQuote?.freight_included || false,
            freight_cost: existingQuote?.freight_cost || "",
            eta_days: existingQuote?.eta_days || existingQuote?.lead_time_days || ""
          };
        });
        setQuotes(initialQuotes);
        
        // Set default valid until date (7 days from now)
        const defaultValidDate = new Date();
        defaultValidDate.setDate(defaultValidDate.getDate() + 7);
        setValidUntil(defaultValidDate.toISOString().split('T')[0]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRequestData();
  }, [token]);

  const updateQuote = (itemId, field, value) => {
    setQuotes(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const handleAcknowledge = async (status) => {
    setAcknowledging(true);
    try {
      const res = await fetch(`${API_URL}/api/suppliers/quote/${token}/acknowledge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: status,
          notes: ackNotes || null,
          estimated_response_date: status === "acknowledged" ? validUntil : null
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to acknowledge");
      }

      setAcknowledged(true);
      setAckStatus(status);
      
      if (status === "not_in_stock") {
        setSubmitted(true);
        toast.success("Response submitted - Items not in stock");
      } else if (status === "need_more_info") {
        setSubmitted(true);
        toast.success("Information request sent");
      } else {
        toast.success("Request acknowledged - Please provide your quotes below");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAcknowledging(false);
    }
  };

  const handleSubmit = async () => {
    // Validate at least one quote has required fields
    const validQuotes = Object.entries(quotes)
      .filter(([_, q]) => q.cost && parseFloat(q.cost) > 0 && q.quantity_available > 0)
      .map(([itemId, q]) => ({
        item_id: itemId,
        item_name: q.item_name || requestData.items.find(i => i.id === itemId)?.item_name || "Unknown Item",
        original_or_substitute: q.original_or_substitute,
        manufacturer_number: q.manufacturer_number || null,
        description: q.description || null,
        quantity_available: parseInt(q.quantity_available) || 0,
        cost: parseFloat(q.cost),
        freight_included: q.freight_included,
        freight_cost: q.freight_cost ? parseFloat(q.freight_cost) : null,
        eta_days: q.eta_days ? parseInt(q.eta_days) : null
      }));

    if (validQuotes.length === 0) {
      toast.error("Please provide at least one quote with cost and quantity");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/suppliers/quote/${token}/submit-enhanced`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quotes: validQuotes,
          supplier_notes: supplierNotes || null,
          valid_until: validUntil || null
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to submit quotes");
      }

      setSubmitted(true);
      toast.success("Quotes submitted successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Request</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-emerald-500/30 rounded-2xl p-8 max-w-md text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">
            {ackStatus === "not_in_stock" ? "Response Recorded" : 
             ackStatus === "need_more_info" ? "Information Request Sent" : 
             "Quotes Submitted!"}
          </h1>
          <p className="text-slate-400 mb-6">
            {ackStatus === "not_in_stock" 
              ? "Thank you for letting us know. We'll reach out to other suppliers."
              : ackStatus === "need_more_info"
              ? "The Sterile Mate team will follow up with additional information shortly."
              : "Thank you for your quote submission. The AskIsaac team will review and reach out if they proceed with your pricing."}
          </p>
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Brain className="w-4 h-4" />
            <span>AskIsaac | </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-600 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">AskIsaac</h1>
              <p className="text-slate-500 text-xs">Supplier Quote Portal | </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border border-cyan-500/20 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Welcome, {requestData.supplier_name}</h2>
              <p className="text-slate-400">
                Sterile Mate has a quote request for the following items. Please review and submit your pricing.
              </p>
            </div>
          </div>
        </div>

        {/* Request Info - BLIND PROTOCOL: Hospital identity masked */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            Request Details
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-slate-900/50 rounded-xl p-4">
              <p className="text-slate-500 text-sm mb-1">Request ID</p>
              <p className="text-white font-mono">#{requestData.request_id.substring(0, 8).toUpperCase()}</p>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-4">
              <p className="text-slate-500 text-sm mb-1">Requester</p>
              <p className="text-white font-medium">AskIsaac Inc.</p>
              <p className="text-slate-500 text-xs">Healthcare Supply Partner</p>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-4">
              <p className="text-slate-500 text-sm mb-1">Priority</p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                requestData.priority === "urgent" ? "bg-red-500/20 text-red-400" :
                requestData.priority === "high" ? "bg-amber-500/20 text-amber-400" :
                "bg-slate-500/20 text-slate-400"
              }`}>
                {requestData.priority?.toUpperCase() || "NORMAL"}
              </span>
            </div>
          </div>
          {/* Delivery Info - Masked */}
          <div className="mt-4 bg-blue-900/20 border border-blue-500/20 rounded-xl p-4">
            <p className="text-blue-400 text-sm font-medium mb-1">📍 Delivery Destination</p>
            <p className="text-white">Sterile Mate Logistics Hub</p>
            <p className="text-slate-400 text-xs">Central Receiving - Shipping details provided upon order confirmation</p>
          </div>
          {requestData.notes && (
            <div className="mt-4 bg-slate-900/50 rounded-xl p-4">
              <p className="text-slate-500 text-sm mb-1">Notes from Hospital</p>
              <p className="text-slate-300">{requestData.notes}</p>
            </div>
          )}
        </div>

        {/* Quick Response Buttons (if not acknowledged) */}
        {!acknowledged && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
              Quick Response
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Please acknowledge receipt of this request. You can then provide detailed quotes below.
            </p>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <Button
                onClick={() => handleAcknowledge("acknowledged")}
                disabled={acknowledging}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ack-available-btn"
              >
                {acknowledging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Can Quote - I Have Items
              </Button>
              <Button
                onClick={() => handleAcknowledge("not_in_stock")}
                disabled={acknowledging}
                variant="outline"
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                data-testid="ack-not-in-stock-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Not in Stock
              </Button>
              <Button
                onClick={() => handleAcknowledge("need_more_info")}
                disabled={acknowledging}
                variant="outline"
                className="border-slate-500/30 text-slate-400 hover:bg-slate-500/10"
                data-testid="ack-need-info-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Need More Info
              </Button>
            </div>
            
            <div>
              <Label className="text-slate-400 text-sm">Notes (optional)</Label>
              <Textarea
                value={ackNotes}
                onChange={(e) => setAckNotes(e.target.value)}
                placeholder="Any questions or comments about this request..."
                className="mt-1 bg-slate-800 border-slate-600 text-white"
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Items to Quote - Only show after acknowledgement or can show anyway */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-400" />
            Items to Quote ({requestData.items.length})
          </h3>
          <p className="text-slate-500 text-sm mb-6">
            For each item, indicate if you&apos;re quoting the original or a substitute, and provide your pricing.
          </p>

          <div className="space-y-6">
            {requestData.items.map((item, index) => (
              <div key={item.id} className="bg-slate-900/50 rounded-xl p-5 border border-slate-700/30">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-cyan-400 text-sm font-mono">#{index + 1}</span>
                      <h4 className="text-white font-semibold text-lg">{item.item_name || item.name}</h4>
                    </div>
                    {/* Manufacturer info - critical for supplier matching */}
                    {(item.manufacturer || item.mfr) && (
                      <p className="text-slate-400 text-sm flex items-center gap-1 mb-1">
                        <Building2 className="w-3 h-3" /> Manufacturer: <span className="text-white font-medium">{item.manufacturer || item.mfr}</span>
                      </p>
                    )}
                    {(item.mfr_number || item.part_number || item.sku) && (
                      <p className="text-slate-500 text-sm flex items-center gap-1">
                        <Hash className="w-3 h-3" /> Part #: <span className="font-mono bg-slate-800 px-2 py-0.5 rounded text-slate-300">{item.mfr_number || item.part_number || item.sku}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm">Qty Requested</p>
                    <p className="text-3xl font-bold text-cyan-400">{item.quantity}</p>
                    <p className="text-slate-500 text-sm">{item.quantity_type || "units"}</p>
                  </div>
                </div>

                {item.notes && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
                    <p className="text-amber-400 text-sm">{item.notes}</p>
                  </div>
                )}

                {/* Quote Form Fields */}
                <div className="space-y-4">
                  {/* Row 1: Item Name & Original/Substitute */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Item Name *</Label>
                      <Input
                        type="text"
                        value={quotes[item.id]?.item_name || ""}
                        onChange={(e) => updateQuote(item.id, "item_name", e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="Item name"
                        data-testid={`quote-item-name-${item.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Original or Substitute *</Label>
                      <Select 
                        value={quotes[item.id]?.original_or_substitute || "original"}
                        onValueChange={(v) => updateQuote(item.id, "original_or_substitute", v)}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid={`quote-type-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="original">Original</SelectItem>
                          <SelectItem value="substitute">Substitute</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 2: Manufacturer Number & Description */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Manufacturer Number</Label>
                      <Input
                        type="text"
                        value={quotes[item.id]?.manufacturer_number || ""}
                        onChange={(e) => updateQuote(item.id, "manufacturer_number", e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="MFR# / Part#"
                        data-testid={`quote-mfr-${item.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Brief Description</Label>
                      <Input
                        type="text"
                        value={quotes[item.id]?.description || ""}
                        onChange={(e) => updateQuote(item.id, "description", e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        placeholder="Short description..."
                        data-testid={`quote-desc-${item.id}`}
                      />
                    </div>
                  </div>

                  {/* Row 3: Quantity Available & Cost */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Quantity Available *</Label>
                      <div className="relative">
                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          min="0"
                          value={quotes[item.id]?.quantity_available || ""}
                          onChange={(e) => updateQuote(item.id, "quantity_available", e.target.value)}
                          className="pl-9 bg-slate-800 border-slate-600 text-white"
                          placeholder="Qty available"
                          data-testid={`quote-qty-${item.id}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Unit Cost ($) *</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={quotes[item.id]?.cost || ""}
                          onChange={(e) => updateQuote(item.id, "cost", e.target.value)}
                          className="pl-9 bg-slate-800 border-slate-600 text-white"
                          placeholder="0.00"
                          data-testid={`quote-cost-${item.id}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 4: Freight & ETA */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">Freight</Label>
                      <Select 
                        value={quotes[item.id]?.freight_included ? "included" : "additional"}
                        onValueChange={(v) => updateQuote(item.id, "freight_included", v === "included")}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid={`quote-freight-type-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="included">Freight Included</SelectItem>
                          <SelectItem value="additional">Additional Charge</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!quotes[item.id]?.freight_included && (
                      <div>
                        <Label className="text-slate-400 text-sm mb-1 block">Freight Cost ($)</Label>
                        <div className="relative">
                          <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={quotes[item.id]?.freight_cost || ""}
                            onChange={(e) => updateQuote(item.id, "freight_cost", e.target.value)}
                            className="pl-9 bg-slate-800 border-slate-600 text-white"
                            placeholder="0.00"
                            data-testid={`quote-freight-cost-${item.id}`}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-slate-400 text-sm mb-1 block">ETA (days)</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          min="0"
                          value={quotes[item.id]?.eta_days || ""}
                          onChange={(e) => updateQuote(item.id, "eta_days", e.target.value)}
                          className="pl-9 bg-slate-800 border-slate-600 text-white"
                          placeholder="e.g. 5"
                          data-testid={`quote-eta-${item.id}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold text-white mb-4">Additional Information</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400 text-sm mb-1 block">Quote Valid Until</Label>
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                data-testid="quote-valid-until"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-sm mb-1 block">Notes for Sterile Mate</Label>
              <Textarea
                value={supplierNotes}
                onChange={(e) => setSupplierNotes(e.target.value)}
                placeholder="Any additional notes about your quote..."
                className="bg-slate-800 border-slate-600 text-white"
                rows={2}
                data-testid="supplier-notes"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-8 py-3 rounded-xl shadow-lg shadow-cyan-500/20"
            data-testid="submit-quotes-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Quotes
              </>
            )}
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-slate-700/50">
          <p className="text-slate-500 text-sm mb-2">
            Quote link expires: {new Date(requestData.token_expires).toLocaleDateString()}
          </p>
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <Brain className="w-4 h-4" />
            <span className="text-sm">AskIsaac | </span>
          </div>
        </div>
      </main>
    </div>
  );
}
